import React from "react";
import { AlertCircle, ChevronRight, Clock3, Gift, MapPin } from "lucide-react";
import { getPromotionGiftMeta } from "./promotionGiftMappers";

function getErrorMessage(error) {
  if (!error) {
    return "";
  }

  if (typeof error === "string") {
    return error;
  }

  return error?.response?.data?.error || error?.message || "정보를 불러오지 못했습니다.";
}

function getSiteSummary(siteLines) {
  if (!siteLines?.length) {
    return "사용가능한 사이트 확인";
  }

  if (siteLines.length === 1) {
    return siteLines[0];
  }

  return `${siteLines[0]} 외 ${siteLines.length - 1}개`;
}

export default function PromotionGiftLayout({
  type,
  itemName,
  imageUrl,
  senderName,
  remainingCount,
  siteLines,
  validityText,
  noticeHtml,
  isLoggedIn,
  isLoading,
  isSubmitting,
  isReceived,
  onPrimaryAction,
  error,
  loggedOutMessage,
  hideSiteSectionWhenEmpty = false,
  sectionTitleOverrides,
  bottomAction,
}) {
  const meta = getPromotionGiftMeta(type);
  const normalizedSiteLines = Array.isArray(siteLines) ? siteLines : [];
  const hasSiteInfo = normalizedSiteLines.length > 0;
  const summaryText = getSiteSummary(normalizedSiteLines);
  const normalizedNoticeHtml = typeof noticeHtml === "string" ? noticeHtml.trim() : "";
  const safeRemainingCount = Number.isFinite(remainingCount) ? remainingCount : 0;
  const hasContent = Boolean(itemName || imageUrl || senderName || hasSiteInfo || normalizedNoticeHtml);
  const errorMessage = getErrorMessage(error);
  const siteSectionTitle = sectionTitleOverrides?.site || "사용가능한 사이트";
  const noticeSectionTitle = sectionTitleOverrides?.notice || "유의사항";
  const shouldRenderSiteSection = hasSiteInfo || !hideSiteSectionWhenEmpty;
  const shouldRenderLocationMeta = hasSiteInfo || !hideSiteSectionWhenEmpty;
  const canTriggerPrimary = typeof onPrimaryAction === "function";

  const primaryLabel = !isLoggedIn
    ? "로그인 하기"
    : isReceived
      ? "이미 선물을 받았습니다."
      : "선물받기";

  const isPrimaryDisabled = isSubmitting || (isLoggedIn && isReceived) || !canTriggerPrimary;
  const shouldShowLoggedOutMessage = !isLoggedIn && !isLoading && !hasContent && Boolean(loggedOutMessage);

  return (
    <div className="min-h-screen bg-[#f5f0e8] px-4 pt-8 pb-12">
      <div className="mx-auto w-full max-w-[640px]">
        <div className="mb-8 flex flex-col items-center text-center">
          <div
            className="mb-6 flex h-24 w-24 items-center justify-center rounded-full text-white shadow-[0_14px_26px_rgba(255,99,74,0.28)]"
            style={{
              background: "linear-gradient(135deg, #ff8a3d 0%, #ff4b5b 100%)",
            }}
          >
            <Gift size={42} strokeWidth={2.2} />
          </div>

          <h1 className="text-[35px] font-semibold leading-tight text-[#152037] sm:text-[42px]" style={{ fontFamily: "Gmarket-M" }}>
            {meta.heroTitle}
          </h1>

          <p className="mt-5 text-[20px] leading-snug text-[#5f6a7c]" style={{ fontFamily: "Gmarket-M" }}>
            {senderName ? (
              <>
                <span className="text-[#ff5d12]">{senderName}</span>
                <span>님이 선물을 보냈습니다.</span>
              </>
            ) : (
              <span>선물 정보를 확인하고 있어요.</span>
            )}
          </p>
        </div>

        {isLoading ? (
          <div className="rounded-[24px] border border-[#eddcc7] bg-white px-6 py-14 text-center text-[20px] text-[#5f6a7c]" style={{ fontFamily: "Gmarket-M" }}>
            로딩중...
          </div>
        ) : shouldShowLoggedOutMessage ? (
          <div className="rounded-[24px] border border-[#eddcc7] bg-white px-6 py-10">
            <p className="text-center text-[20px] text-[#5f6a7c]" style={{ fontFamily: "Gmarket-M" }}>
              {loggedOutMessage}
            </p>
            <button
              type="button"
              disabled={isPrimaryDisabled}
              onClick={onPrimaryAction}
              className="mt-8 flex w-full items-center justify-center gap-3 rounded-[20px] px-6 py-4 text-[18px] text-white transition-opacity disabled:cursor-not-allowed disabled:bg-[#b8c0cd]"
              style={{
                fontFamily: "Gmarket-B",
                background: isPrimaryDisabled
                  ? "#b8c0cd"
                  : "linear-gradient(135deg, #ff7800 0%, #ff2d57 100%)",
              }}
            >
              <Gift size={23} />
              <span>{primaryLabel}</span>
              {!isPrimaryDisabled ? <ChevronRight size={23} /> : null}
            </button>
          </div>
        ) : !hasContent ? (
          <div className="rounded-[24px] border border-[#eddcc7] bg-white px-6 py-14 text-center text-[20px] text-[#5f6a7c]" style={{ fontFamily: "Gmarket-M" }}>
            {errorMessage || "정보를 찾을 수 없습니다."}
          </div>
        ) : (
          <div className="flex flex-col gap-6">
            <div className="overflow-hidden rounded-[30px] border border-[#efdfcc] bg-white shadow-[0_10px_24px_rgba(31,45,61,0.08)]">
              <div className="relative aspect-square w-full overflow-hidden bg-[#d4dbe3]">
                {imageUrl ? (
                  <img src={imageUrl} alt={itemName || "promotion item"} className="h-full w-full object-cover" />
                ) : (
                  <div className="h-full w-full bg-gradient-to-br from-[#cad5e6] to-[#9fb0c8]" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/20 to-transparent" />

                <span
                  className="absolute left-6 rounded-full px-5 py-2 text-[16px] text-white sm:px-6 sm:py-3 sm:text-[20px]"
                  style={{
                    bottom: "80px",
                    fontFamily: "Gmarket-B",
                    background: "linear-gradient(135deg, #ff9a00 0%, #ff5a00 100%)",
                  }}
                >
                  {meta.badgeText}
                </span>

                <p
                  className="absolute left-6 right-6 text-[20px] leading-tight text-white sm:text-[30px]"
                  style={{
                    bottom: "24px",
                    fontFamily: "Gmarket-B",
                    textShadow: "0 2px 10px rgba(0,0,0,0.32)",
                  }}
                >
                  {itemName || "선물 상품"}
                </p>
              </div>

              <div className="flex flex-col gap-4 px-6 py-6">
                {shouldRenderLocationMeta ? (
                  <div className="flex items-center gap-3 text-[#4d586b]">
                    <MapPin size={24} className="shrink-0 text-[#ff8b00]" />
                    <p className="text-[18px] leading-snug" style={{ fontFamily: "Gmarket-M" }}>
                      {summaryText}
                    </p>
                  </div>
                ) : null}

                <div className="flex items-center gap-3 text-[#4d586b]">
                  <Clock3 size={24} className="shrink-0 text-[#ff8b00]" />
                  <p className="text-[18px] leading-snug" style={{ fontFamily: "Gmarket-M" }}>
                    {validityText || meta.defaultValidity}
                  </p>
                </div>

                {isLoggedIn && safeRemainingCount > 1 && !isReceived ? (
                  <p className="text-[17px] text-[#ff5d12]" style={{ fontFamily: "Gmarket-M" }}>
                    남은 선물 {safeRemainingCount}개
                  </p>
                ) : null}

                <button
                  type="button"
                  disabled={isPrimaryDisabled}
                  onClick={onPrimaryAction}
                  className="mt-2 flex w-full items-center justify-center gap-3 rounded-[20px] px-6 py-4 text-[18px] text-white transition-opacity disabled:cursor-not-allowed disabled:bg-[#b8c0cd]"
                  style={{
                    fontFamily: "Gmarket-B",
                    background: isPrimaryDisabled
                      ? "#b8c0cd"
                      : "linear-gradient(135deg, #ff7800 0%, #ff2d57 100%)",
                  }}
                >
                  <Gift size={23} />
                  <span>{primaryLabel}</span>
                  {!isPrimaryDisabled ? <ChevronRight size={23} /> : null}
                </button>
              </div>
            </div>

            {shouldRenderSiteSection ? (
              <div className="overflow-hidden rounded-[24px] border border-[#e4e3e0] bg-white">
                <div className="px-6 py-6">
                  <p className="mb-4 flex items-center gap-2 text-[22px] font-semibold text-[#111d35]" style={{ fontFamily: "Gmarket-M" }}>
                    <MapPin size={25} className="text-[#ff8b00]" />
                    <span>{siteSectionTitle}</span>
                  </p>

                  {hasSiteInfo ? (
                    <div className="flex flex-col gap-3 text-[18px] text-[#4d586b]" style={{ fontFamily: "Gmarket-M" }}>
                      {normalizedSiteLines.map((line, index) => (
                        <p key={`${line}-${index}`}>{line}</p>
                      ))}
                    </div>
                  ) : (
                    <p className="text-[18px] text-[#6b7483]" style={{ fontFamily: "Gmarket-M" }}>
                      사용가능한 사이트 정보가 없습니다.
                    </p>
                  )}
                </div>
              </div>
            ) : null}

            <div className="overflow-hidden rounded-[24px] border border-[#e4e3e0] bg-white">
              <div className="border-b border-[#ececec] px-6 py-6">
                <p className="flex items-center gap-2 text-[22px] font-semibold text-[#111d35]" style={{ fontFamily: "Gmarket-M" }}>
                  <AlertCircle size={25} className="text-[#ff8b00]" />
                  <span>{noticeSectionTitle}</span>
                </p>
              </div>

              <div className="px-6 py-5">
                {normalizedNoticeHtml ? (
                  <div
                    className="text-[18px] leading-relaxed text-[#4d586b]"
                    style={{ fontFamily: "Gmarket-M" }}
                    dangerouslySetInnerHTML={{ __html: normalizedNoticeHtml }}
                  />
                ) : (
                  <p className="text-[18px] text-[#6b7483]" style={{ fontFamily: "Gmarket-M" }}>
                    유의사항 정보가 없습니다.
                  </p>
                )}
              </div>
            </div>

            {bottomAction ? bottomAction : null}
          </div>
        )}
      </div>
    </div>
  );
}
